import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, ArrowRight, ShoppingBag, Wrench } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type'); // 'service' or 'marketplace'
  const requestId = searchParams.get('request_id');
  const productId = searchParams.get('product_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    if (!sessionId || !isAuthenticated) {
      setStatus('failed');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId, isAuthenticated]);

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxPollAttempts) {
      setStatus('failed');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/stripe/checkout/status/${sessionId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      const data = response.data;
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
      } else if (data.status === 'expired') {
        setStatus('failed');
        toast.error('Payment session expired.');
      } else {
        // Still processing, continue polling
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setPollAttempts(prev => prev + 1);
      
      if (pollAttempts < maxPollAttempts - 1) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('failed');
        toast.error('Failed to verify payment status.');
      }
    }
  };

  const handleContinue = () => {
    if (paymentType === 'service' && requestId) {
      navigate(`/customer/tracking/${requestId}`);
    } else if (paymentType === 'marketplace') {
      navigate('/marketplace');
    } else {
      navigate('/customer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-success-page">
      <Header />
      
      <main className="max-w-lg mx-auto px-4 py-16">
        <Card>
          <CardContent className="py-12 text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                  Verifying Payment...
                </h1>
                <p className="text-gray-600 mb-4">
                  Please wait while we confirm your payment.
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((pollAttempts + 1) / maxPollAttempts) * 100}%` }}
                  />
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                  Payment Successful!
                </h1>
                <p className="text-gray-600 mb-6">
                  {paymentType === 'service' 
                    ? 'Your service request has been confirmed. A technician will be assigned shortly.'
                    : 'Your purchase has been completed successfully.'
                  }
                </p>
                
                {paymentDetails && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6 text-left">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-600">Amount Paid</span>
                      <span className="font-bold text-green-700">
                        ${typeof paymentDetails.amount_total === 'number' 
                          ? paymentDetails.amount_total.toFixed(2) 
                          : paymentDetails.amount_total}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status</span>
                      <span className="font-medium text-green-700 capitalize">
                        {paymentDetails.payment_status}
                      </span>
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                  onClick={handleContinue}
                  data-testid="continue-btn"
                >
                  {paymentType === 'service' ? (
                    <>
                      <Wrench className="w-5 h-5 mr-2" />
                      Track Your Service
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Continue Shopping
                    </>
                  )}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 font-['Chivo']">
                  Payment Issue
                </h1>
                <p className="text-gray-600 mb-6">
                  We couldn't verify your payment. If you were charged, please contact our support team.
                </p>
                
                <div className="space-y-3">
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      if (requestId) {
                        navigate(`/customer/tracking/${requestId}`);
                      } else {
                        navigate('/customer');
                      }
                    }}
                    data-testid="try-again-btn"
                  >
                    Try Again
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/customer')}
                    data-testid="go-home-btn"
                  >
                    Go to Dashboard
                  </Button>
                </div>
                
                <p className="text-sm text-gray-500 mt-4">
                  Need help? Call us at <a href="tel:2602183536" className="text-blue-600 font-medium">(260) 218-3536</a>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
